<template>
  <div class="flex flex-col gap-6">
    <div class="flex items-stretch gap-3 overflow-x-auto pb-1">
      <button
        type="button"
        class="flex min-w-[10rem] shrink-0 flex-col rounded-md border p-4 text-left shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-obsidian-300 focus-visible:ring-offset-2"
        :class="
          selection === 'unsettled'
            ? 'border-bittersweet-300 bg-bittersweet-50'
            : 'border-obsidian-100 bg-white hover:border-obsidian-200'
        "
        @click="selectUnsettled"
      >
        <p class="text-sm font-medium text-obsidian-700">{{ t('unsettled') }}</p>
        <div class="mt-2 flex min-h-7 items-center">
          <Spinner v-if="summaryLoading" class="size-5 text-hazel-600" />
          <p v-else class="text-base font-semibold tabular-nums text-obsidian-900">
            {{ formatCurrency(overview?.unsettledPaymentBalance ?? 0) }}
          </p>
        </div>
        <div class="mt-1 flex flex-col gap-0.5 text-xs font-normal leading-snug text-obsidian-400">
          <div>{{ t('paymentCount', { count: overview?.unsettledPaymentCount ?? 0 }) }}</div>
        </div>
      </button>

      <button
        v-for="batch in stripBatches"
        :key="batch.batchNumber"
        type="button"
        class="flex min-w-[10rem] shrink-0 flex-col rounded-md border p-4 text-left shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-obsidian-300 focus-visible:ring-offset-2"
        :class="
          selection === batch.batchNumber
            ? 'border-bittersweet-300 bg-bittersweet-50'
            : 'border-obsidian-100 bg-white hover:border-obsidian-200'
        "
        @click="selectBatch(batch.batchNumber)"
      >
        <p class="flex items-center gap-2 text-sm font-medium text-obsidian-700">
          <span>{{ t('batchLabel', { number: batch.batchNumber }) }}</span>
          <Badge v-if="batch.issueCount > 0" variant="warning" size="sm">
            {{ t('issueCount', { count: batch.issueCount }) }}
          </Badge>
        </p>
        <p class="mt-2 text-base font-semibold tabular-nums text-obsidian-900">
          {{ formatCurrency(batch.payoutAmount) }}
        </p>
        <div class="mt-1 flex flex-col gap-0.5 text-xs font-normal leading-snug text-obsidian-400">
          <div v-for="(line, i) in mutedSummaryLines(batch)" :key="i">{{ line }}</div>
        </div>
      </button>

      <div v-if="batchesLoading" class="flex shrink-0 items-center px-2">
        <Spinner class="size-5 text-hazel-600" />
      </div>

      <div v-else-if="hasNextPage" class="flex shrink-0 items-center">
        <CVButton outline :is-loading="isFetchingNextPage" @click="() => fetchNextPage()">
          {{ t('loadMore') }}
        </CVButton>
      </div>
    </div>

    <EntityListExceptions
      v-if="
        selection !== 'unsettled' && !stripBatches.length && !batchesLoading && !needsPinnedBatch
      "
      :status="batchesStatus"
      :error="batchesError as any"
      :items-count="0"
      :no-items-text="t('noBatches')"
      small
    />

    <UnsettledPaymentsList v-if="selection === 'unsettled'" />
    <SettlementBatchDetailsPane
      v-else-if="typeof selection === 'number'"
      :batch-number="selection"
    />
  </div>
</template>

<script lang="ts" setup>
import CVButton from '@/components/buttons/CVButton.vue';
import Badge from '@/components/Badge.vue';
import Spinner from '@/components/icons/Spinner.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import SettlementBatchDetailsPane from '@/views/receivables/components/SettlementBatchDetailsPane.vue';
import UnsettledPaymentsList from '@/views/receivables/components/UnsettledPaymentsList.vue';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import {
  useSettlementBatch,
  useSettlementBatches,
  useSettlementCashFlow,
} from '@/queries/use-settlement';
import { formatCurrency } from '@/utils/format-numbers';
import type { SettlementBatchListItemDto } from '@carvia/ros-client-types';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const batchQuery = useRouteQueryRef('batch', 'unsettled', 'string');

const summaryQuery = useSettlementCashFlow(true);
const {
  data: batchesData,
  isLoading: batchesLoading,
  status: batchesStatus,
  error: batchesError,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
} = useSettlementBatches(true);

const overview = computed(() => summaryQuery.data.value);
const summaryLoading = computed(() => summaryQuery.isLoading.value);

const loadedBatches = computed(() => batchesData.value?.pages.flatMap((page) => page.items) ?? []);

const selectedBatchNumber = computed(() => {
  const raw = batchQuery.value;
  if (!raw || raw === 'unsettled') return null;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
});

const selection = computed<'unsettled' | number>(() => selectedBatchNumber.value ?? 'unsettled');

const needsPinnedBatch = computed(() => {
  const n = selectedBatchNumber.value;
  if (n == null) return false;
  return !loadedBatches.value.some((b) => b.batchNumber === n);
});

const pinnedBatchQuery = useSettlementBatch(selectedBatchNumber, () => needsPinnedBatch.value);

watch(
  [batchQuery, pinnedBatchQuery.isError, pinnedBatchQuery.isSuccess, pinnedBatchQuery.data],
  () => {
    const raw = batchQuery.value;
    if (!raw || raw === 'unsettled') return;
    const n = Number(raw);
    if (!Number.isInteger(n) || n <= 0) {
      batchQuery.value = 'unsettled';
      return;
    }
    if (!needsPinnedBatch.value) return;
    if (pinnedBatchQuery.isError.value) {
      batchQuery.value = 'unsettled';
    }
  },
  { immediate: true },
);

const stripBatches = computed(() => {
  const loaded = loadedBatches.value;
  const pinned = pinnedBatchQuery.data.value;
  if (pinned && needsPinnedBatch.value) {
    return [pinned, ...loaded.filter((b) => b.batchNumber !== pinned.batchNumber)];
  }
  return loaded;
});

const mutedSummaryLines = (batch: SettlementBatchListItemDto) => {
  const parts = [t('transactionCount', { count: batch.transactionCount })];
  if (batch.invoiceDeductionSum !== 0) {
    parts.push(t('invoiceDeductionPart', { amount: formatCurrency(batch.invoiceDeductionSum) }));
  }
  if (batch.depositCorrectionSum !== 0) {
    parts.push(t('depositCorrectionPart', { amount: formatCurrency(batch.depositCorrectionSum) }));
  }
  if (batch.feesSum !== 0) {
    parts.push(t('feesPart', { amount: formatCurrency(batch.feesSum) }));
  }
  return parts;
};

const selectUnsettled = () => {
  batchQuery.value = 'unsettled';
};

const selectBatch = (batchNumber: number) => {
  batchQuery.value = String(batchNumber);
};
</script>

<i18n lang="json">
{
  "en": {
    "unsettled": "Unsettled",
    "paymentCount": "{count} payments",
    "batchLabel": "Batch {number}",
    "issueCount": "{count} issues",
    "transactionCount": "{count} transactions",
    "invoiceDeductionPart": "invoice deduction {amount}",
    "depositCorrectionPart": "deposit correction {amount}",
    "feesPart": "fees {amount}",
    "loadMore": "Load more",
    "noBatches": "No processed settlement batches yet"
  },
  "de": {
    "unsettled": "Noch nicht abgerechnet",
    "paymentCount": "{count} Zahlungen",
    "batchLabel": "Batch {number}",
    "issueCount": "{count} Probleme",
    "transactionCount": "{count} Transaktionen",
    "invoiceDeductionPart": "Rechnungsabzug {amount}",
    "depositCorrectionPart": "Deposit-Korrektur {amount}",
    "feesPart": "Gebühren {amount}",
    "loadMore": "Mehr laden",
    "noBatches": "Noch keine verarbeiteten Settlement-Batches"
  },
  "es": {
    "unsettled": "Pendiente de liquidar",
    "paymentCount": "{count} pagos",
    "batchLabel": "Lote {number}",
    "issueCount": "{count} incidencias",
    "transactionCount": "{count} transacciones",
    "invoiceDeductionPart": "deducción factura {amount}",
    "depositCorrectionPart": "corrección depósito {amount}",
    "feesPart": "comisiones {amount}",
    "loadMore": "Cargar más",
    "noBatches": "Aún no hay lotes de liquidación procesados"
  }
}
</i18n>
